import React from 'react';
import { ArrowRight, Lightbulb } from 'lucide-react';

/**
 * MccQueueHelp — explains how a derived queue populates.
 *
 * Props:
 *   title    — headline (e.g. "How Editing Queue fills up")
 *   steps    — array of { n, label, hint } explaining each step
 *   ctaLabel — text for the primary jump button
 *   onCta    — callback for that button (usually setActive('...'))
 *   secondaryLabel/onSecondary — optional second button
 */
export default function MccQueueHelp({
  title,
  steps = [],
  ctaLabel,
  onCta,
  secondaryLabel,
  onSecondary,
}) {
  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-slate-50 p-5"
      data-testid="mcc-queue-help"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center">
          <Lightbulb className="w-4 h-4" />
        </div>
        <p className="text-sm font-black uppercase tracking-[0.14em] text-indigo-700">
          {title}
        </p>
      </div>

      <ol className="grid md:grid-cols-4 gap-3 mb-4">
        {steps.map((s, i) => (
          <li
            key={s.n}
            className="relative rounded-xl bg-white border border-slate-200 p-3 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                {s.n}
              </span>
              <p className="text-sm font-bold text-slate-900 leading-tight">{s.label}</p>
            </div>
            {s.hint ? (
              <p className="text-[11px] text-slate-600 leading-snug">{s.hint}</p>
            ) : null}
            {i < steps.length - 1 && (
              <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 z-10" />
            )}
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        {ctaLabel && onCta ? (
          <button
            onClick={onCta}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold"
            data-testid="mcc-queue-help-cta"
          >
            {ctaLabel} <ArrowRight className="w-3 h-3" />
          </button>
        ) : null}
        {secondaryLabel && onSecondary ? (
          <button
            onClick={onSecondary}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-slate-400 text-slate-800 text-xs font-bold"
            data-testid="mcc-queue-help-secondary"
          >
            {secondaryLabel} <ArrowRight className="w-3 h-3" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
